/**
 * Présentation « lisible par tous » des données de migration.
 *
 * Le visualiseur de lot doit être compréhensible en première lecture par un
 * public non technique : ce module traduit les clés techniques des records
 * (sourceData / mappedData / targetData) en libellés français et formate les
 * valeurs (dates, booléens, listes). La vue technique (JSON brut) reste
 * disponible en seconde lecture.
 *
 * Tout est générique : les colonnes et les correspondances V1→V2 sont
 * dérivées du `fieldMapping` du lot (`{fields: {champV2: {from: champV1}}}`),
 * les libellés viennent du dictionnaire ci-dessous (avec humanisation de
 * repli pour les clés inconnues).
 */
import type { MigrationRecord, RecordStatus } from '../types';

/** Options du filtre statut, partagées entre les onglets Données et Fichiers. */
export const recordStatusOptions: { value: RecordStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'pending', label: 'En attente' },
  { value: 'mapped', label: 'Mappé' },
  { value: 'validated', label: 'Validé' },
  { value: 'migrated', label: 'Migré' },
  { value: 'failed', label: 'Échoué' },
  { value: 'excluded', label: 'Exclu' },
  { value: 'review_needed', label: 'À vérifier' },
];

/** Libellés FR des champs (mappedData / sourceData), toutes familles de lots. */
export const FIELD_LABELS: Record<string, string> = {
  // Identité
  firstname: 'Prénom',
  lastname: 'Nom',
  email: 'E-mail',
  dateOfBirth: 'Date de naissance',
  date_of_birth: 'Date de naissance',
  sex: 'Sexe',
  phone: 'Téléphone',
  mobile: 'Téléphone mobile',
  address: 'Adresse',
  zipcode: 'Code postal',
  city: 'Ville',
  country: 'Pays',
  // Génériques
  name: 'Nom',
  label: 'Libellé',
  title: 'Titre',
  description: 'Description',
  code: 'Code',
  status: 'Statut',
  createDate: 'Date de création',
  created_at: 'Créé le',
  sortOrder: 'Ordre',
  displayOrder: "Ordre d'affichage",
  isActive: 'Actif',
  // Scolarité / structure
  className: 'Classe',
  class_name: 'Classe',
  classId: 'Classe (id V1)',
  class_id: 'Classe (id V1)',
  levelName: 'Niveau',
  level: 'Niveau',
  cycle: 'Cycle',
  section: 'Section',
  capacity: 'Capacité',
  comment: 'Commentaire',
  order: 'Ordre',
  // Classes (mappedData) — libellés métier des champs référence
  mainTeacherId: 'Enseignant principal',
  additionalTeachersCsv: 'Enseignants additionnels',
  roomId: 'Salle',
  parentProgramIdsCsv: 'Niveau(x)',
  sourceSectionId: 'Section V1 (id)',
  studentId: 'Élève (id V1)',
  studentIds: 'Élèves rattachés (ids V1)',
  teacherIds: 'Enseignants (ids V1)',
  schoolYearId: 'Année scolaire (id V1)',
  school_year_id: 'Année scolaire (id V1)',
  parentID: 'Parent (id V1)',
  parentId: 'Parent (id V1)',
  groupName: 'Groupe',
  group_name: 'Groupe',
  // Établissements
  etablissmentID: 'Établissement (id V1)',
  optionsJson: 'Options V1 (JSON)',
  options_json: 'Options V1 (JSON)',
  active: 'Actif',
  sectionIdsCsv: 'Sections V1 (ids)',
  section_ids_csv: 'Sections V1 (ids)',
  groupeIdsCsv: 'Groupes V1 (ids)',
  groupe_ids_csv: 'Groupes V1 (ids)',
  matchedBy: 'Rapproché par',
  champsAppliques: 'Champs appliqués',
  nonMigres: 'Non migrés (info)',
  sectionsV1: 'Sections V1 (ids)',
  rne: 'RNE',
  iban: 'IBAN',
  bic: 'BIC',
  ics: 'ICS (créancier SEPA)',
  code_academie: 'Code académie',
  libelle_academie: 'Académie',
  postal_address: 'Adresse postale',
  formatFacture: 'Format n° facture',
  formatAvoir: 'Format n° avoir',
  formatAuxiliaire: 'Format compte auxiliaire',
  formatAuxiliaireEleve: 'Format compte auxiliaire élève',
  piedDePageFacture: 'Pied de page facture',
  adresseFacturation: 'Adresse de facturation',
  logoFichier: 'Logo (fichier V1)',
  groupesV1: 'Groupes V1 (ids)',
  subjectName: 'Matière',
  subject_name: 'Matière',
  teacherName: 'Enseignant',
  teacher_name: 'Enseignant',
  roomName: 'Salle',
  roomTypeName: 'Type de salle',
  // Emplois du temps
  dayOfWeek: 'Jour',
  startTime: 'Heure de début',
  endTime: 'Heure de fin',
  startDate: 'Date de début',
  endDate: 'Date de fin',
  recurrence: 'Récurrence',
  // Fichiers
  photoFilename: 'Fichier photo',
  photo_filename: 'Fichier photo',
  originalName: "Nom d'origine",
  storedName: 'Fichier stocké',
  fileName: 'Fichier',
  file_name: 'Fichier',
  typeTitle: 'Type de document',
  mimeType: 'Type de fichier',
  size: 'Taille',
  // Modèles de formulaires
  formName: 'Nom du formulaire',
  schoolYear: 'Année scolaire',
  recipientType: 'Destinataires en V1',
  familyUse: 'Une réponse par famille',
  isAdministrative: 'Formulaire administratif',
  isSchool: 'Inscription scolaire',
  isExtra: 'Inscription périscolaire',
  v1Status: 'Actif en V1',
  signatureType: 'Type de signature',
  doubleSignature: 'Double signature',
  showParent: 'Proposé aux responsables',
  showStudent: 'Proposé aux élèves',
  popupContent: 'Texte affiché avant la réponse',
  nbFields: 'Nombre de champs',
  migratedFields: 'Champs repris',
  v1Fields: 'Champs en V1',
  dynamicFormId: 'Formulaire V2 associé',
  sectionId: 'Page du formulaire',
  // Références croisées migration
  originStep: "Lot d'origine",
  originSourceId: "Id V1 dans le lot d'origine",
  messageV1Id: 'Message V1 (id)',
  userV1Id: 'Utilisateur V1 (id)',
  parentsV1Id: 'Foyer V1 (id)',
  source_id: 'Id source V1',
  sourceId: 'Id source V1',
  // Montessori
  codeDomain: 'Code domaine',
  aireName: 'Aire',
  domaineName: 'Domaine',
  activityName: 'Activité',
  competenceName: 'Compétence',
  statusName: 'Statut de suivi',
  // Messagerie / notifications
  subject: 'Sujet',
  body: 'Contenu',
  senderName: 'Expéditeur',
  recipients: 'Destinataires',
  // Facturation
  defaultAmount: 'Montant par défaut (HT)',
  note: 'Code analytique (note V1)',
  tvaRate: 'Taux de TVA (%)',
  tvaName: 'TVA (nom V1)',
  accountCode: 'Compte comptable (code)',
  accountLabel: 'Compte comptable (libellé)',
  accountCategory: 'Catégorie comptable V1',
  familyName: "Famille d'article",
  scheduleLabel: 'Échéancier par défaut',
  discountType: 'Type de remise V1',
  isPrivate: 'Article système V1',
  reference: 'Référence système V1',
  defaultQuantity: 'Quantité par défaut',
  recurringFee: 'Facturation automatique',
  hasBillingslice: 'Soumis à une tranche',
  isCantine: 'Article cantine par défaut',
  paymentDay: "Jour d'échéance",
  monthsCsv: 'Mois facturés (V1)',
  rulesCsv: 'Grille tarifaire (article:périodicité:montant)',
  v2Method: 'Moyen de paiement V2',
  v1Sources: 'Traces V1',
  documentType: 'Type de document',
  formatMask: 'Masque de numérotation V1',
  codeRestart: 'Remise à zéro du compteur',
  numberedTotal: 'Documents numérotés (total V1)',
  numberedThisYear: 'Documents numérotés (année en cours V1)',
  lastCode: 'Dernier numéro V1',
  isCurrent: 'Exercice en cours',
  // Facturation — identité, gabarits, payeurs, factures de solde
  schoolName: "Nom de l'école (V1)",
  headOfficeAddress: 'Adresse du siège (V1)',
  templateType: 'Type de document',
  invoiceFooterHtml: 'Pied de page des factures V1 (HTML)',
  globalFooter: 'Pied de page général V1',
  footerShow: 'Pied de page affiché sur les factures',
  showLogo: 'Logo affiché (V1)',
  showNumber: 'Numéro affiché (V1)',
  kind: 'Nature du payeur',
  v1Id: 'Identifiant V1',
  displayName: 'Nom',
  email2: 'E-mail (mère)',
  companyName: 'Raison sociale',
  siret: 'SIRET',
  birthDate: 'Date de naissance',
  openInvoices: 'Factures V1 non soldées',
  maininvoiceId: 'Facture V1 (id)',
  invoiceDate: 'Date de la facture V1',
  schoolYearName: 'Année scolaire',
  studentName: 'Élève',
  payerName: 'Payeur (V1)',
  payerType: 'Type de payeur V1',
  parentsId: 'Foyer (id V1)',
  thirdPartyId: 'Tiers (id V1)',
  totalTtc: 'Total TTC V1',
  totalTva: 'TVA V1',
  paid: 'Réglé (V1)',
  credits: 'Avoirs (V1)',
  fines: 'Pénalités (V1)',
  waivers: 'Remises sur règlement (V1)',
  remaining: 'Reste à payer',
  openDeadlines: 'Échéances V1 restantes',
  totalDeadlines: 'Échéances V1 (total)',
  openDueDates: 'Dates des échéances restantes',
  // Facturation — SEPA
  sepaStatus: 'Prélèvement SEPA actif (V1)',
  activeMandates: 'Mandats actifs (V1)',
  rum: 'RUM (V1)',
  debtorName: 'Débiteur (nom V1)',
  debtorAddress: 'Débiteur (adresse V1)',
  bankName: 'Banque du débiteur',
  parentsType: 'Signataire (père / mère / les deux)',
  validUntil: 'Fin de validité (V1)',
  signatureFile: 'Fichier de signature (V1)',
  signatureBase64: 'Signature (image encodée)',
  isLatestForPayer: 'Mandat le plus récent du payeur',
  latestSepaId: 'Mandat V1 retenu (id)',
  latestRum: 'Mandat V1 retenu (RUM)',
  schoolAddress: "Adresse de l'école (V1)",
  studentNames: 'Élèves du foyer',
  schoolYearV1Id: 'Année scolaire (id V1)',
  libelle: 'Libellé',
  salesType: 'Marqué « ventes » (V1)',
  commentaire: 'Commentaire',
  parentCode: 'Compte parent (code)',
  rate: 'Taux (%)',
  isDefault: 'Taux par défaut',
};

/** Libellés FR des clés de résultat V2 (targetData). */
export const TARGET_FIELD_LABELS: Record<string, string> = {
  schoolUserId: 'Utilisateur V2 (SchoolUser)',
  userRoleId: 'Rôle utilisateur V2 (UserRole)',
  userRoleIds: 'Rôles utilisateur V2 (UserRole)',
  userAccountId: 'Compte de connexion V2',
  globalUserId: 'Utilisateur global V2',
  linkedParents: 'Parents liés',
  v2FileId: 'Fichier V2',
  schoolFileId: 'Fichier V2 (school_file)',
  messageId: 'Message V2',
  threadId: 'Conversation V2',
  assessmentId: 'Suivi V2',
  logoUrl: 'URL du logo V2',
  admFileId: 'Fichier ADM',
  admPublicUrl: 'URL publique ADM',
  agendaId: 'Agenda V2',
  slotIds: 'Créneaux V2',
  levelId: 'Niveau V2',
  cycleId: 'Cycle V2',
  schoolClassId: 'Classe V2',
  subjectId: 'Matière V2',
  subjectGroupId: 'Groupe de matières V2',
  roomId: 'Salle V2',
  roomTypeId: 'Type de salle V2',
  userGroupId: "Groupe d'élèves V2",
  typeCode: 'Type de document V2',
  orphan: 'Sans propriétaire (visible admin uniquement)',
  provisioned: 'Compte provisionné',
  father: 'Père',
  mother: 'Mère',
  error: 'Anomalie',
  warning: 'Avertissement',
  // Facturation
  reusedExisting: 'Existant V2 réutilisé',
  updatedExisting: 'Existant V2 synchronisé',
  taxRateId: 'Taux de TVA V2',
  revenueAccountId: 'Compte de produit V2',
  revenueCategoryId: 'Catégorie de recette V2',
  unitPriceExcludingTax: 'Prix unitaire HT',
  ruleCount: 'Règles tarifaires posées',
  skipped: 'Articles sautés',
  v1Periodicities: 'Périodicités V1',
  paymentMethod: 'Moyen de paiement',
  numberOfInstallments: "Nombre d'échéances",
  v1Months: 'Mois V1',
  v1PaymentDay: "Jour d'échéance V1",
  truncatedTo12: 'Plafonné à 12 échéances',
  v1Mask: 'Masque V1',
  unsupportedTokens: 'Jetons V1 sans équivalent',
  prefix: 'Préfixe',
  dateFormat: 'Format de date',
  yearFormat: "Format d'année",
  counterDigits: 'Chiffres du compteur',
  resetCounterPerFiscalYear: 'Remise à zéro par exercice',
  separator: 'Séparateur',
  startingNumber: 'Premier numéro',
  v1Name: 'Nom V1',
  v1Description: 'Description V1',
  collectedTaxAccountCode: 'Compte de TVA collectée (code)',
  importedInBatch: 'Créé par l’import en masse',
  derivedFromInvoiceMask: 'Dérivé du masque des factures',
  v1CountedDocuments: 'Documents V1 comptés',
  v1MaxCounter: 'Plus grand compteur V1 émis',
  v1LastCode: 'Dernier numéro V1',
  v1CodesMatchingMask: 'Numéros V1 conformes au masque',
  v1CodesNotMatchingMask: 'Numéros V1 hors masque',
  organizationName: "Nom de l'organisation",
  organizationAddress: "Adresse de l'organisation",
  rnaNumber: 'Numéro RNA',
  v1Rne: 'RNE (V1)',
  legalMentions: 'Mentions légales',
  footerMessage: 'Message de bas de page',
  v1ShowLogo: 'Logo affiché en V1',
  v1ShowNumber: 'Numéro affiché en V1',
  payerType: 'Type de payeur V2',
  guardianUserRoleIds: 'Responsables V2 (UserRole)',
  studentUserRoleId: 'Élève V2 (UserRole)',
  studentUserRoleIds: 'Élèves V2 rattachés (UserRole)',
  openInvoices: 'Factures V1 non soldées',
  verified: 'Cible V2 revérifiée',
  v1InvoiceId: 'Facture V1 (id)',
  v1Code: 'Numéro de la facture V1',
  v1InvoiceDate: 'Date de la facture V1',
  v1TotalTtc: 'Total TTC V1',
  v1Paid: 'Réglé en V1',
  v1Credits: 'Avoirs V1',
  remaining: 'Reste à payer repris',
  openDeadlinesV1: 'Échéances V1 restantes',
  installments: 'Mensualités V2',
  payerId: 'Payeur V2',
  payerKey: 'Payeur (clé du lot Payeurs)',
  fiscalYearId: 'Exercice V2',
  productId: 'Article technique V2',
  scheduleTemplateId: 'Modèle d’échéancier V2',
  scheduleTemplateName: 'Modèle d’échéancier',
  documentNumber: 'Numéro de facture V2',
  totalIncludingTax: 'Total TTC V2',
  v1LockdownAppliedByThisLot: 'Facturation V1 fermée par ce lot',
  remittanceIban: 'IBAN de remise (masqué)',
  remittanceBic: 'BIC de remise',
  v1SepaEnabled: 'Prélèvement activé en V1',
  isComplete: 'Configuration complète',
  v2Rum: 'RUM V2 (générée)',
  v1Rum: 'RUM V1 (historique)',
  debtorNameV1: 'Débiteur (nom V1)',
  debtorIbanLast4: 'IBAN débiteur (4 derniers)',
  scheme: 'Schéma SEPA',
  sequence: 'Séquence',
  signatureDate: 'Date de signature',
  v1ValidUntil: 'Fin de validité V1',
  v1ParentsType: 'Signataire V1',
  origin: 'Origine du consentement',
  studentsAttached: 'Élèves rattachés au mandat',
  proofSource: 'Source de la preuve',
};

/** Humanise une clé inconnue : camelCase / snake_case → « Mots espacés ». */
function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? TARGET_FIELD_LABELS[key] ?? humanizeKey(key);
}

export function targetFieldLabel(key: string): string {
  return TARGET_FIELD_LABELS[key] ?? FIELD_LABELS[key] ?? humanizeKey(key);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?)?/;

/** Formate une valeur pour l'affichage lisible (dates FR, booléens, listes…). */
export function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    if (ISO_DATE.test(value)) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        const hasTime = value.length > 10;
        return d.toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          ...(hasTime ? { hour: '2-digit', minute: '2-digit' } : {}),
        });
      }
    }
    return value;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    if (value.every((v) => typeof v !== 'object' || v === null)) {
      return value.map((v) => formatValue(v)).join(', ');
    }
    return `${value.length} élément${value.length > 1 ? 's' : ''}`;
  }
  // Objet : résumé compact (le détail est dans la vue technique).
  const keys = Object.keys(value as Record<string, unknown>);
  return keys.length === 0 ? '—' : `${keys.length} champ${keys.length > 1 ? 's' : ''}`;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

/**
 * Titre lisible d'un record : identité (prénom + nom), sinon premier champ
 * « nommant » disponible, sinon l'id source.
 */
export function recordTitle(record: MigrationRecord): string {
  const data = { ...(record.sourceData ?? {}), ...(record.mappedData ?? {}) } as Record<string, unknown>;
  const firstname = data.firstname ?? data.prenom;
  const lastname = data.lastname ?? data.nom;
  if (typeof firstname === 'string' && typeof lastname === 'string' && (firstname || lastname)) {
    return `${firstname} ${lastname}`.trim();
  }
  for (const key of ['name', 'label', 'title', 'originalName', 'photoFilename', 'fileName', 'storedName', 'subject', 'code']) {
    const v = data[key];
    if (typeof v === 'string' && v.trim() !== '') return v;
  }
  return `#${record.sourceId}`;
}

/** Structure de fieldMapping des lots : `{fields: {champV2: {from, transform}}}`. */
interface FieldMappingConfig {
  fields?: Record<string, { from?: string; transform?: string; required?: boolean }>;
}

/** Extrait la map `champV2 → champV1` du fieldMapping d'un lot. */
export function mappingFields(
  fieldMapping: Record<string, unknown> | null | undefined
): { v2Field: string; v1Field: string | null; transform: string | null; required: boolean }[] {
  const fields = (fieldMapping as FieldMappingConfig | null)?.fields;
  if (!fields || typeof fields !== 'object') return [];
  return Object.entries(fields).map(([v2Field, config]) => ({
    v2Field,
    v1Field: config?.from ?? null,
    transform: config?.transform ?? null,
    required: !!config?.required,
  }));
}

export interface CorrespondenceRow {
  /** Clé technique V2 (mappedData). */
  field: string;
  /** Libellé lisible. */
  label: string;
  /** Clé technique V1 (sourceData). */
  v1Field: string | null;
  v1Value: unknown;
  v2Value: unknown;
  /** Valeurs différentes après transformation (info, pas une erreur). */
  transformed: boolean;
}

/**
 * Correspondance champ à champ V1 → V2 d'un record, dérivée du fieldMapping
 * du lot. C'est la vue « comprendre ce que devient chaque donnée ».
 */
export function buildCorrespondence(
  record: MigrationRecord,
  fieldMapping: Record<string, unknown> | null | undefined
): CorrespondenceRow[] {
  const source = (record.sourceData ?? {}) as Record<string, unknown>;
  const mapped = (record.mappedData ?? {}) as Record<string, unknown>;
  const fields = mappingFields(fieldMapping);

  if (fields.length > 0) {
    return fields.map(({ v2Field, v1Field }) => {
      const v1Value = v1Field ? source[v1Field] : undefined;
      const v2Value = mapped[v2Field];
      return {
        field: v2Field,
        label: fieldLabel(v2Field),
        v1Field,
        v1Value,
        v2Value,
        transformed:
          v1Value !== undefined && v2Value !== undefined && String(v1Value ?? '') !== String(v2Value ?? ''),
      };
    });
  }

  // Repli sans fieldMapping : aligne les clés de mappedData sur sourceData.
  return Object.entries(mapped).map(([key, v2Value]) => ({
    field: key,
    label: fieldLabel(key),
    v1Field: key in source ? key : null,
    v1Value: source[key],
    v2Value,
    transformed: false,
  }));
}

/**
 * Colonnes lisibles pour le tableau des records d'un lot : les premiers
 * champs du fieldMapping (les plus significatifs), plafonnés pour rester
 * lisibles. Exclut les champs purement techniques (références croisées).
 */
const TECHNICAL_FIELDS = new Set(['originStep', 'originSourceId', 'messageV1Id', 'userV1Id', 'parentsV1Id', 'sourceId', 'source_id']);

export function readableColumns(
  fieldMapping: Record<string, unknown> | null | undefined,
  max = 4
): { key: string; label: string }[] {
  return mappingFields(fieldMapping)
    .filter(({ v2Field }) => !TECHNICAL_FIELDS.has(v2Field))
    .slice(0, max)
    .map(({ v2Field }) => ({ key: v2Field, label: fieldLabel(v2Field) }));
}

/**
 * Aplati targetData en lignes lisibles (clé → valeur), en remontant les
 * anomalies imbriquées (clés `error` / `warning`) pour qu'elles soient
 * visibles en première lecture.
 */
export interface TargetRow {
  label: string;
  value: string;
  isError: boolean;
  /** Id technique V2 (uuid) — affiché en style mono. */
  isId: boolean;
}

const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function buildTargetRows(
  targetData: Record<string, unknown> | null | undefined,
  prefix = ''
): TargetRow[] {
  if (!targetData) return [];
  const rows: TargetRow[] = [];
  for (const [key, value] of Object.entries(targetData)) {
    const label = prefix ? `${prefix} — ${targetFieldLabel(key)}` : targetFieldLabel(key);
    const isError = key === 'error' || key === 'warning';
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      rows.push(...buildTargetRows(value as Record<string, unknown>, label));
      continue;
    }
    rows.push({
      label,
      value: formatValue(value),
      isError,
      isId: typeof value === 'string' && UUID_LIKE.test(value),
    });
  }
  return rows;
}
